// Shared rating-cell primitives used by any roster/leader/player-detail
// table that ships the legacy color tiers + stacked current/potential
// display. Lifted from `features/team/roster/columns.tsx` when
// `features/league/players` became the second consumer.

export interface Rating {
  current?: string | null
  potential?: string | null
}

// Bold/muted swap on the stacked cell — which row of the pair reads as
// the headline value. Roster's sortable rating columns flip this when
// the user toggles sort emphasis.
export type RatingEmphasis = 'current' | 'potential'

// Legacy site rating-tier color palette, keyed by tier. A value like 92 reads
// bright-red, 67 reads black, 24 reads dark-blue across every roster /
// free-agent / leader table on the site.
//
// The tier is derived HERE, from the value being rendered. The API deliberately
// does not ship one: a tier whose only consumer is a Tailwind class map is
// presentation, and presentation is the client's (see the UI/API boundary rule
// in `.claude/rules/react-migration.md`).
export const RATING_LEVEL_COLOR: Record<string, string> = {
  'very high': 'text-[#FF1414]',
  high:        'text-[#AF0000]',
  average:     'text-black',
  low:         'text-[#0000AF]',
  'very low':  'text-[#00007D]',
}

/**
 * Tailwind text-color class for a rating VALUE ("87", "B+", null).
 *
 * ⚠️ Named `…ForValue` deliberately. This replaced a `ratingColor(level)` that
 * took the API's pre-computed tier string, and the two signatures are
 * interchangeable to the compiler — a missed call site would have silently
 * rendered black (a tier string like "high" parses as NaN, misses GRADE_LEVEL,
 * and falls through to the default) with no type error. The rename forces the
 * compiler to find every one. Don't rename it back.
 */
export function ratingColorForValue(value?: string | null): string {
  const level = levelFromValue(value)
  if (!level) return 'text-black'
  return RATING_LEVEL_COLOR[level] ?? 'text-black'
}

// Map a letter grade (A+ … F) to a rating tier: A's → very high, B's → high,
// C's → average, D's → low, E/F → very low. This is the ONLY implementation —
// it was verified equivalent to the server's former GetLetterRatingLevel (whose
// 85/70/55/40 cutoffs map exactly onto these letter bands) before that was
// deleted, so no colour changed for letter-grade leagues.
// E is the backend's lowest DISPLAY grade (PlayerRating.GetLetterGradeDisplay,
// still server-side); F is the FE sort scale's lowest — both are the bottom tier.
const GRADE_LEVEL: Record<string, string> = {
  'A+': 'very high', A: 'very high', 'A-': 'very high',
  'B+': 'high', B: 'high', 'B-': 'high',
  'C+': 'average', C: 'average', 'C-': 'average',
  'D+': 'low', D: 'low', 'D-': 'low',
  E: 'very low', F: 'very low',
}

// Derive a rating tier from a raw value string. This is now the ONLY
// implementation — the server's copy was deleted along with the `currentLevel` /
// `potentialLevel` fields it fed.
//
// Numeric boundaries are the legacy Rating.js valueToDegree thresholds. They
// differ from the server's former numeric thresholds at exactly two values:
// **20** (was low, now very low) and **40** (was average, now low). That shift
// was accepted deliberately when the server copy was deleted; the alternative
// was keeping a second implementation alive to preserve two boundary values.
//
// Letter grades resolve through GRADE_LEVEL rather than these numeric cutoffs —
// a real second scale, not a duplicate (a "D" covers 40–54 and is `low`, while
// the numeric 40 is also `low` but 54 would be `average`). Unknown → undefined.
export function levelFromValue(value?: string | null): string | undefined {
  if (!value) return undefined
  const n = parseFloat(value)
  if (Number.isNaN(n)) return GRADE_LEVEL[value.trim().toUpperCase()]
  if (n >= 85) return 'very high'
  if (n >= 70) return 'high'
  if (n >= 41) return 'average'
  if (n >= 21) return 'low'
  return 'very low'
}

// Build a Rating from raw value/potential strings. Now a plain shape adapter —
// it used to also synthesize the tier levels, which RatingCell derives itself.
export function ratingFromValues(
  current?: string | null,
  potential?: string | null,
): Rating {
  return { current: current ?? null, potential: potential ?? null }
}

// Letter-grade leagues ship ratings as letters instead of 0–99 numbers, so
// `parseInt('A+')` is NaN and the column can't sort. Rank the grades
// best→worst; the index gives each a descending sort value (A+ highest) so the
// column orders A+ > A > A- > B+ > … > E. Within a league all values are one
// type (all numeric OR all letters), so these ranks never collide with the
// 0–99 numeric scale.
//
// **The server's scale ends at "E"**, not "F" (`PlayerRating.GetLetterGradeDisplay`
// — A+ ≥95 … D- ≥40, else "E"). "E" was missing here while `GRADE_LEVEL` above
// had it, so `GRADE_RANK['E']` was undefined and `ratingNum`'s `?? 0` scored
// every E-rated player as 0 — sorted tied with unrated players, while still
// colored correctly. `F` is kept ranked below `E` purely defensively: it never
// arrives on the wire, but if it ever did it should still outrank "no rating".
const GRADE_ORDER = [
  'A+', 'A', 'A-', 'B+', 'B', 'B-', 'C+', 'C', 'C-', 'D+', 'D', 'D-', 'E', 'F',
] as const
const GRADE_RANK: Record<string, number> = Object.fromEntries(
  GRADE_ORDER.map((g, i) => [g, GRADE_ORDER.length - i]),
)

// Numeric value of a rating in the chosen emphasis ('current' or
// 'potential'). Used by TanStack's accessor / sortingFn so a rating
// column can flip between sorting by actual vs potential without a
// column rebuild. Numeric leagues sort by the 0–99 value; letter-grade
// leagues sort by GRADE_RANK. Null/missing/unknown → 0 (sorts to bottom
// under desc).
export function ratingNum(
  r: Rating | null | undefined,
  mode: RatingEmphasis,
): number {
  if (!r) return 0
  const raw = mode === 'current' ? r.current : r.potential
  if (!raw) return 0
  const n = parseInt(raw, 10)
  if (!Number.isNaN(n)) return n
  return GRADE_RANK[raw.trim().toUpperCase()] ?? 0
}

// Stacked rating cell — current on top, potential below. When
// `emphasis === 'potential'`, the bold/muted treatment flips so the
// potential row reads as the headline value while the current row reads
// as the secondary value. Mirrors the 5173 prototype's behavior.
export function RatingCell({
  rating,
  emphasis = 'current',
  showPotential = true,
}: {
  rating?: Rating
  emphasis?: RatingEmphasis
  showPotential?: boolean
}) {
  const c = rating?.current ?? ''
  const p = rating?.potential ?? ''
  if (!c && !p) return <span className="text-slate-400">—</span>
  const currentColor = ratingColorForValue(c)
  const potentialColor = ratingColorForValue(p)
  const currentClass = !showPotential
    ? `font-semibold ${currentColor} opacity-85`
    : emphasis === 'current'
      ? `font-semibold ${currentColor} opacity-85`
      : `text-[13px] ${currentColor} opacity-70`
  const potentialClass =
    emphasis === 'potential'
      ? `font-semibold ${potentialColor} opacity-85`
      : `text-[13px] ${potentialColor} opacity-70`
  return (
    <div className="flex flex-col leading-tight">
      <span className={currentClass}>{c}</span>
      {showPotential && p && <span className={potentialClass}>{p}</span>}
    </div>
  )
}

// Background tints applied to a column's header + cells to group rating
// columns into sections. Colors and alphas match the 5173 prototype.
// Section tints use /60 alpha so they layer correctly over row-level
// backgrounds (regular stripes, cut-row terracotta). At alpha against
// white the result is a slightly washier version of the same earth
// tone — visually consistent with the editorial direction while letting
// row-level color (cut-row red, sort-tint navy) show through.
export type RatingSection = 'overalls' | 'batting' | 'pitching' | 'fielding' | 'endurance'

export const SECTION_BG: Record<RatingSection, string> = {
  overalls:  'bg-[#e8eef3]/60',
  batting:   'bg-[#f4e7d8]/60',
  pitching:  'bg-[#ecdee2]/60',
  fielding:  'bg-[#dfe7dc]/60',
  endurance: 'bg-[#f0e9d5]/60',
}

// Opaque equivalent of SECTION_BG — each color is the result of compositing
// the /60-alpha tint over white. Visually identical to SECTION_BG against
// the default white page background; use on header cells when the thead
// is sticky (at /60 alpha, body rows scrolling under a sticky header show
// through). Body td cells keep the /60 variant so they still layer over
// row stripes / cut-row tints.
export const SECTION_BG_OPAQUE: Record<RatingSection, string> = {
  overalls:  '!bg-[#f1f4f7]',
  batting:   '!bg-[#f8f0e7]',
  pitching:  '!bg-[#f4ebed]',
  fielding:  '!bg-[#ecf1ea]',
  endurance: '!bg-[#f6f2e6]',
}

// Raw opaque hex equivalents of SECTION_BG_OPAQUE (no Tailwind wrapper). Passed
// to DataTable's `meta.stickyHeaderBgColor`, which paints the sticky header
// background as an inline style — a sticky header must be opaque so body rows
// don't bleed through the translucent /60 SECTION_BG tint while scrolling.
export const SECTION_BG_COLOR: Record<RatingSection, string> = {
  overalls:  '#f1f4f7',
  batting:   '#f8f0e7',
  pitching:  '#f4ebed',
  fielding:  '#ecf1ea',
  endurance: '#f6f2e6',
}

// The section-summary columns — the grand OVR (whole `overalls` section) and
// each section's lead overall column (OVR b/p/f) — render as a slightly DARKER
// shade of their own section tint, so the summary reads apart from the
// individual skills in the same section. Same hue as SECTION_BG, deeper.
export const SECTION_BG_STRONG: Record<RatingSection, string> = {
  overalls:  'bg-[#d3dfe8]/60',
  batting:   'bg-[#e6cba5]/60',
  pitching:  'bg-[#d9bcc4]/60',
  fielding:  'bg-[#c3d4ba]/60',
  endurance: 'bg-[#e6d9b3]/60',
}
// Opaque composite of each SECTION_BG_STRONG tint over white (sticky-header bg),
// mirroring SECTION_BG_COLOR.
export const SECTION_BG_STRONG_COLOR: Record<RatingSection, string> = {
  overalls:  '#e5ecf1',
  batting:   '#f0e0c9',
  pitching:  '#e8d7dc',
  fielding:  '#dbe5d5',
  endurance: '#f0e7cd',
}

// A section-lead overall column's id/key: "overall" or "potential" (draft ships
// potential-only, so its summaries are named potentialHitting/…) followed by a
// section name. The grand OVR is detected by section instead (it lives in the
// `overalls` section on every table).
const SECTION_LEAD_OVERALL = /^(overall|potential)(hitting|pitching|fielding)$/i

/** True for section-summary columns (grand OVR or a section's lead overall) —
 *  these take the deeper SECTION_BG_STRONG shade. Pass the column's id/key. */
export function isSectionOverall(section: RatingSection, key?: string | null): boolean {
  return section === 'overalls' || (!!key && SECTION_LEAD_OVERALL.test(key))
}

/** Section tint class, deeper for section-summary columns. Drop-in for
 *  `SECTION_BG[section]` where a column id/key is available. */
export function sectionTint(section: RatingSection, key?: string | null): string {
  return isSectionOverall(section, key) ? SECTION_BG_STRONG[section] : SECTION_BG[section]
}

/** Opaque section bg color (sticky header), deeper for section-summary columns.
 *  Drop-in for `SECTION_BG_COLOR[section]`. */
export function sectionTintColor(section: RatingSection, key?: string | null): string {
  return isSectionOverall(section, key) ? SECTION_BG_STRONG_COLOR[section] : SECTION_BG_COLOR[section]
}
